// "Guests asked" — real questions from the inbox / chatbot with the hotel's plain answers (published by staff, 7 languages).
import React, { useEffect, useState } from 'react';
import { MessageCircleQuestion, ChevronDown } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const API = process.env.REACT_APP_BACKEND_URL || '';

const T = {
  en: { title: 'Guests asked us', lead: 'Real questions from guests, answered by the reception. Shortened only to remove personal details.', asked: 'asked', src: { contact: 'by e-mail', chat: 'in the website chat', manual: 'at reception' } },
  me: { title: 'Gosti su nas pitali', lead: 'Prava pitanja gostiju, odgovara recepcija. Skraćeno samo radi uklanjanja ličnih podataka.', asked: 'pitano', src: { contact: 'e-mailom', chat: 'u chatu na sajtu', manual: 'na recepciji' } },
  sq: { title: 'Mysafirët na pyetën', lead: 'Pyetje të vërteta nga mysafirët, përgjigjur nga recepsioni. Shkurtuar vetëm për të hequr të dhënat personale.', asked: 'pyetur', src: { contact: 'me e-mail', chat: 'në chat-in e faqes', manual: 'në recepsion' } },
  de: { title: 'Gäste haben uns gefragt', lead: 'Echte Fragen von Gästen, beantwortet von der Rezeption. Nur um persönliche Daten gekürzt.', asked: 'gefragt', src: { contact: 'per E-Mail', chat: 'im Website-Chat', manual: 'an der Rezeption' } },
  ru: { title: 'Гости спрашивали', lead: 'Настоящие вопросы гостей, отвечает ресепшен. Сокращено только для удаления личных данных.', asked: 'спросили', src: { contact: 'по e-mail', chat: 'в чате на сайте', manual: 'на ресепшен' } },
  pl: { title: 'Goście pytali', lead: 'Prawdziwe pytania gości, odpowiada recepcja. Skrócone tylko o dane osobowe.', asked: 'zapytano', src: { contact: 'e-mailem', chat: 'na czacie strony', manual: 'w recepcji' } },
  hu: { title: 'Vendégeink kérdezték', lead: 'Valódi vendégkérdések, a recepció válaszaival. Csak a személyes adatokat távolítottuk el.', asked: 'kérdezve', src: { contact: 'e-mailben', chat: 'a weboldal chatjében', manual: 'a recepción' } },
};

const QaItem = ({ item, t, open, onToggle }) => (
  <li className="border border-gray-200 rounded-xl bg-white overflow-hidden" data-testid={`guests-asked-item-${item.id}`}>
    <button type="button" onClick={onToggle} className="w-full flex items-start gap-3 text-left px-4 py-3 hover:bg-gray-50 transition-colors" aria-expanded={open} data-testid={`guests-asked-q-${item.id}`}>
      <MessageCircleQuestion className="w-5 h-5 text-orange-500 flex-shrink-0 mt-0.5" />
      <span className="flex-1 text-sm md:text-base font-semibold text-gray-900">{item.q}</span>
      <ChevronDown className={`w-4 h-4 text-gray-400 flex-shrink-0 mt-1 transition-transform ${open ? 'rotate-180' : ''}`} />
    </button>
    {open && (
      <div className="px-4 pb-4 pl-12">
        <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-line" data-testid={`guests-asked-a-${item.id}`}>{item.a}</p>
        <p className="text-[11px] text-gray-400 mt-2">{t.asked} {t.src[item.source] || ''}{item.asked_month ? ` · ${item.asked_month}` : ''}</p>
      </div>
    )}
  </li>
);

const GuestsAsked = ({ roomId = null, compact = false, limit = 8, className = '' }) => {
  const { lang } = useLanguage();
  const t = T[lang] || T.en;
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(null);
  useEffect(() => {
    let live = true;
    const qs = `lang=${lang}&limit=${limit}${roomId != null ? `&room_id=${roomId}` : ''}`;
    fetch(`${API}/api/guest-qa?${qs}`).then((r) => (r.ok ? r.json() : null)).then((j) => { if (live) setItems(j?.items || []); }).catch(() => { if (live) setItems([]); });
    return () => { live = false; };
  }, [lang, roomId, limit]);
  if (!items || !items.length) return null;
  const rows = items.map((it) => <QaItem key={it.id} item={it} t={t} open={open === it.id} onToggle={() => setOpen(open === it.id ? null : it.id)} />);
  const body = (
    <>
      <h2 className={`${compact ? 'text-lg' : 'text-2xl md:text-3xl'} font-bold text-gray-900`} data-testid="guests-asked-title">{t.title}</h2>
      <p className="text-sm text-gray-500 mt-1 mb-4 max-w-2xl">{t.lead}</p>
      <ul className="space-y-2">{rows}</ul>
    </>
  );
  if (compact) return <div className={`rounded-2xl border border-gray-200 bg-white p-5 md:p-6 ${className}`} data-testid="guests-asked">{body}</div>;
  return (
    <section className="py-12 md:py-16 bg-[#faf7f2]" data-testid="guests-asked">
      <div className="container mx-auto px-4 max-w-4xl">{body}</div>
    </section>
  );
};

export default GuestsAsked;
